import React, { useState } from 'react'
import styled from '@emotion/styled'

import { GlobalStyle } from '../components/GlobalStyle'
import { ConfigProvider } from '../contexts/Config'
import { ShopProvider } from '../contexts/Shop'
import { ApolloProvider } from '../contexts/Apollo'
import { ThemeProvider } from '../contexts/Theme'
import { AppProvider } from '../contexts/App'
import { ViewportSettings } from './ViewportSettings'
import { Canvas } from './Canvas'
import { SaveAttempt } from './SaveAttempt'
import { ConfigureSidebar, StyleSidebar } from './Sidebar'
import { HeadingToolbar } from './HeadingToolbar'
import { ScopeProvider } from '../components/FrameProvider'
import { IframeProvider } from '../contexts/Iframe/Iframe'
import { Radio } from 'antd'
import { PagePills } from './PagePills'

type PropsTodo = any

export const Root: React.FC<PropsTodo> = () => {
  const options = [
    { label: 'Web', value: 'web' },
    { label: 'Mobile App', value: 'mobile', disabled: true },
    { label: 'Email', value: 'email', disabled: true }
  ]

  return (
    <>
      <ConfigProvider>
        <ApolloProvider>
          <IframeProvider>
            <ShopProvider>
              <ScopeProvider scope={'#root'}>
                <ThemeProvider>
                  <div className='app'>
                    <AppProvider>
                      <LayoutContainer>
                        <Header>
                          <StyledRadioGroup
                            options={options}
                            optionType='button'
                            buttonStyle='solid'
                            defaultValue='web'
                          />
                          <PagePills />
                        </Header>

                        <Main>
                          <StyleSidebar />
                          <Canvas />
                          <SaveAttempt />
                          <ConfigureSidebar />
                          <CanvasToolbar>
                            <ToolbarContainer>
                              <ViewportSettings />
                            </ToolbarContainer>
                            <ToolbarContainer>
                              <HeadingToolbar />
                            </ToolbarContainer>
                          </CanvasToolbar>
                        </Main>
                      </LayoutContainer>
                    </AppProvider>
                    <GlobalStyle />
                  </div>
                </ThemeProvider>
              </ScopeProvider>
            </ShopProvider>
          </IframeProvider>
        </ApolloProvider>
      </ConfigProvider>
    </>
  )
}

const LayoutContainer = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  height: 100vh;
  max-height: calc(100vh - 280px);
`

const Main = styled.div`
  position: relative;

  display: flex;
  flex-flow: row;
  width: 100%;
  height: 100%;

  @keyframes openContent {
    from {
      width: 20px;
    }

    to {
      width: 25vw;
    }
  }

  @keyframes closeContent {
    from {
      width: 25vw;
    }

    to {
      width: 20px;
    }
  }

  @keyframes openOpacity {
    0% {
      opacity: 0;
    }

    40% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  @keyframes closeOpacity {
    0% {
      opacity: 1;
    }

    40% {
      opacity: 0;
    }

    100% {
      opacity: 0;
    }
  }

  @keyframes openStyleLabel {
    from {
      right: 0;
    }

    to {
      right: -12px;
    }
  }

  @keyframes closeStyleLabel {
    from {
      right: -12px;
    }

    to {
      right: 0;
    }
  }

  @keyframes openConfigureLabel {
    from {
      left: 4px;
    }

    to {
      left: -12px;
    }
  }

  @keyframes closeConfigureLabel {
    from {
      left: -12px;
    }

    to {
      left: 4px;
    }
  }

  @keyframes activeToolbarLabel {
    from {
      color: #dbd9df;
    }

    to {
      color: #8c8ca1;
    }
  }

  @keyframes inactiveToolbarLabel {
    from {
      color: #8c8ca1;
    }

    to {
      color: #dbd9df;
    }
  }
`

const CanvasToolbar = styled.div`
  position: fixed;
  bottom: 7.5vh;
  left: 50%;
  z-index: 3000;

  display: flex;
  gap: 8px;

  transform: translateX(-50%);
`

const StyledRadioGroup = styled(Radio.Group)`
  padding: 2px;
  border-radius: 4px;
  background: ${({ theme }: any) => theme.colors.background25};

  label {
    border: none;
  }

  .ant-radio-button-wrapper.ant-radio-button-wrapper-checked {
    border-radius: 4px;
  }

  label span {
    border-radius: 4px;
  }

  label:hover {
    span {
      color: white;
    }
  }

  .ant-radio-button-wrapper:not(:first-child)::before {
    display: none;
  }
`

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;

  border-radius: 4px;
  border: 1px solid ${({ theme }: any) => theme.colors.background50};
  background: ${({ theme }: any) => theme.colors.background25};
`

const Header = styled.div`
  display: flex;
  flex-flow: row;
  width: 100%;
  padding: 16px 24px;
  justify-content: space-between;
  background-color: ${({ theme }: any) => theme.colors.white};
`
